import { useTranslation } from "react-i18next";
import { Text } from "@core/text";
import { Database, CheckCircle } from "lucide-react";
import { useMemo } from "react";
import { useGetMaarifLearningOutcomes } from "@packages/api/madlenAPI";
import { GetTopicsCourseType } from "@packages/api/madlenAPI.schemas";
import { CourseData } from "@packages/constant/CourseData";

interface UnitData {
  alanBecerileri?: string;
  anahtarKavramlar?: string;
  becerilerArasiIliskiler?: string;
  degerler?: string;
  destekleme?: string;
  disiplinlerArasiIliskiler?: string;
  egilimler?: string;
  kavramsalBeceriler?: string;
  kopruKurma?: string;
  okuryazarlikBecerileri?: string;
  onDegerlendirmeSureci?: string;
  sosyalDuygusalOgrenmeBecerileri?: string;
  temelKabuller?: string;
  zenginlestirme?: string;
}

interface UbdMaarifDataStepProps {
  gradeLevel: number;
  lesson: string;
  selectedOutcomes: { id: string; text: string }[];
}

const UbdMaarifDataStep: React.FC<UbdMaarifDataStepProps> = ({
  gradeLevel,
  lesson,
  selectedOutcomes,
}) => {
  const { t } = useTranslation();

  // Fetch real Maarif data from API
  const { data: maarifOutcomesData } = useGetMaarifLearningOutcomes({
    grade: gradeLevel,
    courseType: lesson as GetTopicsCourseType,
  });

  // Get units that contain selected learning outcomes
  const selectedUnits = useMemo(() => {
    return maarifOutcomesData?.units?.filter((unit) =>
      unit.outcomes?.find((outcome) =>
        selectedOutcomes
          .map((a) => a.id)
          .includes(outcome.id?.toString() || ""),
      ),
    );
  }, [maarifOutcomesData, selectedOutcomes]);

  // Function to combine column data from selected units
  const getCombinedColumnData = (columnKey: keyof UnitData): string[] => {
    if (!selectedUnits?.length) return [];

    return selectedUnits
      .reduce<string[]>((acc, unit) => {
        const value = unit[columnKey];
        if (Array.isArray(value)) {
          return [...acc, ...value.filter(Boolean)];
        }
        if (value) {
          return [...acc, value];
        }
        return acc;
      }, [])
      .filter(Boolean);
  };

  // Create maarif data from selected units
  const maarifData = useMemo(() => {
    return {
      alanBecerileri: getCombinedColumnData("alanBecerileri"),
      becerilerArasiIliskiler: getCombinedColumnData("becerilerArasiIliskiler"),
      destekleme: getCombinedColumnData("destekleme").join(" "),
      egilimler: getCombinedColumnData("egilimler"),
      okuryazarlikBecerileri: getCombinedColumnData("okuryazarlikBecerileri"),
      sosyalDuygusalOgrenme: getCombinedColumnData(
        "sosyalDuygusalOgrenmeBecerileri",
      ),
      koprukurma: getCombinedColumnData("kopruKurma").join(" "),
      zenginlestirme: getCombinedColumnData("zenginlestirme").join(" "),
      anahtarKavramlar: getCombinedColumnData("anahtarKavramlar").join(", "),
      degerler: getCombinedColumnData("degerler"),
      disiplinlerArasiIliskiler: getCombinedColumnData(
        "disiplinlerArasiIliskiler",
      ),
      kavramsalBecerileri: getCombinedColumnData("kavramsalBeceriler"),
      onDegerlendirme: getCombinedColumnData("onDegerlendirmeSureci").join(" "),
      temelKabuller: getCombinedColumnData("temelKabuller").join(" "),
    };
  }, [selectedUnits]);

  // Get translated course name
  const getCourseName = () => {
    const courseInfo = CourseData[lesson as keyof typeof CourseData];
    if (courseInfo) {
      return t(`courseType.${courseInfo.name(gradeLevel)}`);
    }
    return lesson;
  };

  const DataSection: React.FC<{
    title: string;
    items: string | string[];
    isArray?: boolean;
    color?: string;
  }> = ({ title, items, isArray = Array.isArray(items), color = "blue" }) => {
    // Don't render if data is empty
    if (isArray && (!items || (items as string[]).length === 0)) return null;
    if (!isArray && (!items || (items as string).trim() === "")) return null;

    const colorClasses = {
      blue: {
        bg: "bg-blue-50 dark:bg-blue-900/20",
        border: "border-blue-200 dark:border-blue-700/30",
        icon: "text-blue-500",
        title: "text-blue-700 dark:text-blue-300",
        tag: "bg-blue-100 dark:bg-blue-800/30 text-blue-700 dark:text-blue-300",
      },
      green: {
        bg: "bg-green-50 dark:bg-green-900/20",
        border: "border-green-200 dark:border-green-700/30",
        icon: "text-green-500",
        title: "text-green-700 dark:text-green-300",
        tag: "bg-green-100 dark:bg-green-800/30 text-green-700 dark:text-green-300",
      },
      purple: {
        bg: "bg-purple-50 dark:bg-purple-900/20",
        border: "border-purple-200 dark:border-purple-700/30",
        icon: "text-purple-500",
        title: "text-purple-700 dark:text-purple-300",
        tag: "bg-purple-100 dark:bg-purple-800/30 text-purple-700 dark:text-purple-300",
      },
      orange: {
        bg: "bg-orange-50 dark:bg-orange-900/20",
        border: "border-orange-200 dark:border-orange-700/30",
        icon: "text-orange-500",
        title: "text-orange-700 dark:text-orange-300",
        tag: "bg-orange-100 dark:bg-orange-800/30 text-orange-700 dark:text-orange-300",
      },
      teal: {
        bg: "bg-teal-50 dark:bg-teal-900/20",
        border: "border-teal-200 dark:border-teal-700/30",
        icon: "text-teal-500",
        title: "text-teal-700 dark:text-teal-300",
        tag: "bg-teal-100 dark:bg-teal-800/30 text-teal-700 dark:text-teal-300",
      },
      pink: {
        bg: "bg-pink-50 dark:bg-pink-900/20",
        border: "border-pink-200 dark:border-pink-700/30",
        icon: "text-pink-500",
        title: "text-pink-700 dark:text-pink-300",
        tag: "bg-pink-100 dark:bg-pink-800/30 text-pink-700 dark:text-pink-300",
      },
      indigo: {
        bg: "bg-indigo-50 dark:bg-indigo-900/20",
        border: "border-indigo-200 dark:border-indigo-700/30",
        icon: "text-indigo-500",
        title: "text-indigo-700 dark:text-indigo-300",
        tag: "bg-indigo-100 dark:bg-indigo-800/30 text-indigo-700 dark:text-indigo-300",
      },
      emerald: {
        bg: "bg-emerald-50 dark:bg-emerald-900/20",
        border: "border-emerald-200 dark:border-emerald-700/30",
        icon: "text-emerald-500",
        title: "text-emerald-700 dark:text-emerald-300",
        tag: "bg-emerald-100 dark:bg-emerald-800/30 text-emerald-700 dark:text-emerald-300",
      },
    };

    const colorClass =
      colorClasses[color as keyof typeof colorClasses] || colorClasses.blue;

    return (
      <div
        className={`${colorClass.bg} ${colorClass.border} backdrop-blur-sm rounded-xl p-4 border shadow-sm`}
      >
        <div className="flex items-center space-x-2 mb-3">
          <CheckCircle className={`w-4 h-4 ${colorClass.icon}`} />
          <Text
            variant="h3"
            className={`text-sm font-semibold ${colorClass.title}`}
          >
            {title}
          </Text>
        </div>
        {isArray ? (
          <div className="flex flex-wrap gap-2">
            {(items as string[]).map((item, index) => (
              <span
                key={index}
                className={`px-2 py-1 ${colorClass.tag} rounded-lg text-xs font-medium`}
              >
                {item}
              </span>
            ))}
          </div>
        ) : (
          <Text
            variant="p"
            size="sm"
            className="text-gray-600 dark:text-gray-300 leading-relaxed"
          >
            {items as string}
          </Text>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl p-6 border border-white/30 dark:border-gray-700/30 shadow-lg">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg shadow-sm">
              <Database className="w-6 h-6 text-white" />
            </div>
            <div>
              <Text
                variant="h1"
                className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"
              >
                Maarif Program Verileri
              </Text>
              <Text
                variant="p"
                size="sm"
                className="text-gray-600 dark:text-gray-300"
              >
                {gradeLevel}. Sınıf {getCourseName()}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Maarif Data Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <DataSection
          title="Alan Becerileri"
          items={maarifData.alanBecerileri}
          color="blue"
        />
        <DataSection
          title="Beceriler Arası İlişkiler"
          items={maarifData.becerilerArasiIliskiler}
          color="green"
        />
        <DataSection
          title="Eğilimler"
          items={maarifData.egilimler}
          color="purple"
        />
        <DataSection
          title="Okuryazarlık Becerileri"
          items={maarifData.okuryazarlikBecerileri}
          color="orange"
        />
        <DataSection
          title="Sosyal Duygusal Öğrenme"
          items={maarifData.sosyalDuygusalOgrenme}
          color="teal"
        />
        <DataSection
          title="Değerler"
          items={maarifData.degerler}
          color="pink"
        />
        <DataSection
          title="Disiplinler Arası İlişkiler"
          items={maarifData.disiplinlerArasiIliskiler}
          color="indigo"
        />
        <DataSection
          title="Kavramsal Beceriler"
          items={maarifData.kavramsalBecerileri}
          color="emerald"
        />
      </div>

      {/* Text-based Data */}
      <div className="grid grid-cols-1 gap-4">
        <DataSection
          title="Anahtar Kavramlar"
          items={maarifData.anahtarKavramlar}
          isArray={false}
          color="blue"
        />
        <DataSection
          title="Destekleme"
          items={maarifData.destekleme}
          isArray={false}
          color="green"
        />
        <DataSection
          title="Köprü Kurma"
          items={maarifData.koprukurma}
          isArray={false}
          color="purple"
        />
        <DataSection
          title="Zenginleştirme"
          items={maarifData.zenginlestirme}
          isArray={false}
          color="orange"
        />
        <DataSection
          title="Ön Değerlendirme Süreci"
          items={maarifData.onDegerlendirme}
          isArray={false}
          color="teal"
        />
        <DataSection
          title="Temel Kabuller"
          items={maarifData.temelKabuller}
          isArray={false}
          color="pink"
        />
      </div>
    </div>
  );
};

export default UbdMaarifDataStep;
